import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Steps, Button, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useHistory } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { slices } from 'store/slices'
import Wrapper from 'components/reusables/wrapper'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import { useNotification } from 'hooks/notifications/useNotification'
import { learningPathsService } from 'api/administration/learningPaths'
import {
  initialLearningPathConfiguration,
  initialLearningPathContent,
} from 'store/slices/initialData'
import { LearningPathStatus } from 'enum/learningPathStatus.enum'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import { Header, StepsContainer, Buttons } from './styled'
import Content from './content'
import Configuration from './configuration'

const { Step } = Steps

const LearningPathsManage = () => {
  const { t } = useTranslation()
  const prefix = 'administration.learningPaths.manage'
  const history = useHistory()
  const dispatch = useAppDispatch()
  const notification = useNotification()
  const params = useParams<{ id: string }>()
  const id = params.id
  const isUpdate = !!id
  const [currentStep, setCurrentStep] = useState(0)
  const [isGoingBack, setIsGoingBack] = useState(false)
  const [isGoingToFirstStep, setIsGoingToFirstStep] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [hasAddedCourses, setHasAddedCourses] = useState(false)
  const configuration = useAppSelector(slices.learningPath.configuration)
  const configurationUpdated = useAppSelector(
    slices.learningPath.configurationUpdated
  )
  const content = useAppSelector(slices.learningPath.content)
  const contentUpdated = useAppSelector(slices.learningPath.contentUpdated)

  const getConfiguration = useCallback(async () => {
    if (id) {
      dispatch(slices.learningPath.setLoadingConfiguration(true))
      await learningPathsService.getConfiguration(id).then((response) => {
        dispatch(slices.learningPath.setConfiguration(response))
      })
      dispatch(slices.learningPath.setLoadingConfiguration(false))
    } else {
      dispatch(
        slices.learningPath.setConfiguration(initialLearningPathConfiguration)
      )
    }
  }, [dispatch, id])

  useEffect(() => {
    getConfiguration()
  }, [getConfiguration])

  useEffect(() => {
    setHasAddedCourses(false)
    for (const collection of content.collections) {
      if (collection.relatedCourses.length) {
        setHasAddedCourses(true)
        break
      }
    }
  }, [content.collections])

  const getContent = async () => {
    if (id) {
      dispatch(slices.learningPath.setLoadingContent(true))
      await learningPathsService.getContent(id).then((response) => {
        if (Object.keys(response).length) {
          dispatch(slices.learningPath.setContent(response))
          if (response.learningPaths.length === 0) {
            dispatch(slices.learningPath.addEmptyLearningPath())
          }
        } else {
          dispatch(slices.learningPath.setContent(initialLearningPathContent))
        }
      })
      dispatch(slices.learningPath.setLoadingContent(false))
    } else {
      dispatch(slices.learningPath.setContent(initialLearningPathContent))
    }
  }

  const saveConfiguration = async () => {
    if (configurationUpdated) {
      if (configuration.name.trim().length === 0) {
        notification.warning(t(`${prefix}.notifications.requiredProgramName`))
        setIsGoingBack(false)
        return
      }
      setProcessing(true)
      const finalConfiguration = getFinalConfiguration()
      await learningPathsService
        .saveConfiguration(finalConfiguration)
        .then((response) => {
          notification.success(
            t(`${prefix}.notifications.changesSavedSuccessfully`)
          )
          finalConfiguration.id = response.id
          dispatch(slices.learningPath.setConfiguration(finalConfiguration))
          dispatch(slices.learningPath.updateItem(response))
        })
      setProcessing(false)
    }
    if (isGoingBack) {
      history.push('/administration/learning-paths')
    } else {
      setIsGoingBack(false)
      getContent()
      setCurrentStep(1)
    }
  }

  const getFinalConfiguration = () => {
    return {
      id: configuration.id,
      name: configuration.name.trim(),
      description: configuration.description.trim(),
      image: configuration.image,
      profiles: configuration.profiles,
      state: configuration.state,
    }
  }

  const saveContent = async (learningPathState: number) => {
    try {
      if (contentUpdated) {
        if (
          learningPathState === LearningPathStatus.published &&
          !hasAddedCourses
        ) {
          notification.warning(t(`${prefix}.notifications.withoutCourses`))
          setIsGoingBack(false)
          setIsGoingToFirstStep(false)
          return
        }
        setProcessing(true)
        await learningPathsService
          .saveContent(
            configuration.id,
            learningPathState,
            content.learningPaths,
            content.collections
          )
          .then((response) => {
            notification.success(
              t(`${prefix}.notifications.prerequisitesSaved`)
            )
            dispatch(slices.learningPath.updateItem(response))
          })
      }
    } catch (error) {
      return ErrorToast(t('administration.toastMessages.createError'))
    } finally {
      setProcessing(false)
    }

    if (isGoingToFirstStep) {
      setIsGoingToFirstStep(false)
      setCurrentStep(0)
    } else {
      history.push('/administration/learning-paths')
    }
  }

  const goBack = () => {
    if (configurationUpdated || contentUpdated) {
      setIsGoingBack(true)
    } else {
      history.push('/administration/learning-paths')
    }
  }

  const goToFirstStep = () => {
    if (contentUpdated) {
      setIsGoingToFirstStep(true)
    } else {
      setCurrentStep(0)
    }
  }

  const goToFirstStepAndClose = () => {
    dispatch(slices.learningPath.setContentUpdated(false))
    setIsGoingToFirstStep(false)
    setCurrentStep(0)
  }

  return (
    <Wrapper shadow={true}>
      <Header>
        <FontAwesomeIcon onClick={goBack} icon={faArrowLeft as IconProp} />
        <span>
          {isUpdate ? t(`${prefix}.update.title`) : t(`${prefix}.create.title`)}
        </span>
      </Header>
      <StepsContainer>
        <Steps size="small" current={currentStep}>
          <Step
            title={t(`${prefix}.steps.configuration.title`)}
            icon={
              currentStep > 0 ? (
                <FontAwesomeIcon icon={faCheck as IconProp} />
              ) : undefined
            }
          />
          <Step
            title={t(`${prefix}.steps.content.title`)}
            icon={
              currentStep > 1 ? (
                <FontAwesomeIcon icon={faCheck as IconProp} />
              ) : undefined
            }
          />
        </Steps>
      </StepsContainer>
      {currentStep === 0 && <Configuration />}
      {currentStep === 1 && <Content />}
      <Buttons>
        <Spin
          spinning={
            configuration.state === LearningPathStatus.draft &&
            currentStep === 1 &&
            processing
          }
        >
          {configuration.state === LearningPathStatus.draft &&
            currentStep === 1 && (
              <Button
                onClick={() => saveContent(LearningPathStatus.draft)}
                disabled={!contentUpdated}
              >
                {t(`${prefix}.create.content.buttons.tertiary`)}
              </Button>
            )}
        </Spin>
        <Spin spinning={processing}>
          {currentStep === 0 ? (
            <Button
              onClick={saveConfiguration}
              disabled={configuration.name.trim().length === 0}
            >
              {isUpdate
                ? t(`${prefix}.update.configuration.buttons.primary`)
                : t(`${prefix}.create.configuration.buttons.primary`)}
            </Button>
          ) : (
            <Button
              onClick={() => saveContent(LearningPathStatus.published)}
              disabled={!hasAddedCourses}
            >
              {isUpdate
                ? t(`${prefix}.update.content.buttons.primary`)
                : t(`${prefix}.create.content.buttons.primary`)}
            </Button>
          )}
        </Spin>
        <Spin spinning={processing}>
          {currentStep === 0 ? (
            <Button onClick={goBack}>
              {t(`${prefix}.create.configuration.buttons.secondary`)}
            </Button>
          ) : (
            <Button onClick={goToFirstStep}>
              {t(`${prefix}.create.content.buttons.secondary`)}
            </Button>
          )}
        </Spin>
      </Buttons>
      <Modal
        isOpen={isGoingBack || isGoingToFirstStep}
        title={t(`${prefix}.create.configuration.confirmation.title`)}
        isConfirmation={true}
        maxheight={400}
        maxwidth={800}
        actions={
          <ModalButtons
            primaryText={t(
              `${prefix}.create.configuration.confirmation.buttons.primary`
            )}
            primaryAction={() =>
              currentStep === 0
                ? saveConfiguration()
                : saveContent(configuration.state)
            }
            secondaryText={t(
              `${prefix}.create.configuration.confirmation.buttons.secondary`
            )}
            secondaryAction={() =>
              isGoingBack
                ? history.push('/administration/learning-paths')
                : goToFirstStepAndClose()
            }
          />
        }
        close={() =>
          isGoingBack ? setIsGoingBack(false) : setIsGoingToFirstStep(false)
        }
      >
        {currentStep === 0
          ? isUpdate
            ? `${t(`${prefix}.update.configuration.confirmation.message`)}`
            : `${t(`${prefix}.create.configuration.confirmation.message`)}`
          : isUpdate
          ? `${t(`${prefix}.update.content.confirmation.message`)}`
          : `${t(`${prefix}.create.content.confirmation.message`)}`}
      </Modal>
    </Wrapper>
  )
}

export default LearningPathsManage
